/**
 * Analytics Utilities - Lightweight Implementation for Core Kernel
 *
 * Provides session tracking, event buffering, user identity,
 * and page view tracking. Events are logged to console in dev mode
 * and stored in an in-memory buffer for diagnostics.
 * A full analytics SDK can be enabled via plugins for production use.
 */

import { logger } from './logger';

const log = logger.scope('Analytics');

// Types for analytics events
export interface AnalyticsEvent {
  name: string;
  properties?: Record<string, unknown>;
  timestamp?: number;
}

export interface AnalyticsUser {
  id: string;
  email?: string;
  name?: string;
  properties?: Record<string, unknown>;
}

// Configuration
const ENVIRONMENT = import.meta.env.VITE_ENV || 'development';

// ── Module-level state ────────────────────────────────────

let _sessionId: string | null = null;
let _currentUser: AnalyticsUser | null = null;
let _userProperties: Record<string, unknown> = {};
let _optedOut = false;
let _initialized = false;

const MAX_BUFFER_SIZE = 500;
const _eventBuffer: AnalyticsEvent[] = [];

// ── Helpers ───────────────────────────────────────────────

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback UUID v4 generation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function bufferEvent(event: AnalyticsEvent): void {
  _eventBuffer.push(event);
  if (_eventBuffer.length > MAX_BUFFER_SIZE) {
    _eventBuffer.splice(0, _eventBuffer.length - MAX_BUFFER_SIZE);
  }
}

// ── Public API ────────────────────────────────────────────

/**
 * Initialize analytics — generates a session ID and sets up page view tracking.
 */
export async function initAnalytics(): Promise<void> {
  if (_initialized) return;
  _initialized = true;

  _sessionId = generateUUID();
  log.log('Initialized', { sessionId: _sessionId, environment: ENVIRONMENT });

  // Track initial page view
  if (typeof window !== 'undefined') {
    trackPageView(window.location.pathname + window.location.search);
  }
}

/**
 * Track an analytics event.
 * Logs to console in dev mode, always stores in the event buffer.
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
): void {
  if (_optedOut) return;

  const event: AnalyticsEvent = {
    name: eventName,
    properties: {
      ...properties,
      sessionId: _sessionId,
      userId: _currentUser?.id,
      timestamp: Date.now(),
    },
    timestamp: Date.now(),
  };

  bufferEvent(event);

  if (ENVIRONMENT === 'development') {
    log.debug('Event:', eventName, properties);
  }
}

/**
 * Identify a user for event enrichment.
 */
export function identifyUser(user: AnalyticsUser): void {
  _currentUser = user;
  if (user.properties) {
    _userProperties = { ..._userProperties, ...user.properties };
  }
  log.log('User identified:', user.id);
  trackEvent('user_identified', { userId: user.id });
}

/**
 * Reset analytics session — clears session ID, user, and buffer.
 */
export function resetAnalytics(): void {
  const oldSessionId = _sessionId;
  _sessionId = generateUUID();
  _currentUser = null;
  _userProperties = {};
  _eventBuffer.length = 0;
  _initialized = false;
  log.log('Session reset', { oldSessionId, newSessionId: _sessionId });
}

/**
 * Opt out of analytics tracking.
 */
export function optOut(): void {
  _optedOut = true;
  log.log('Opted out');
}

/**
 * Opt in to analytics tracking.
 */
export function optIn(): void {
  _optedOut = false;
  log.log('Opted in');
}

/**
 * Check if a feature flag is enabled (always returns undefined).
 */
export function isFeatureEnabled(feature: string): boolean | undefined {
  return undefined;
}

/**
 * Track a page view with page name and optional properties.
 */
export function trackPageView(pageName: string, properties?: Record<string, unknown>): void {
  if (_optedOut) return;

  if (ENVIRONMENT === 'development') {
    log.debug('Page view:', pageName, properties);
  }
  trackEvent('page_view', { page: pageName, ...properties });
}

/**
 * Track a user action — wraps trackEvent with an action category.
 */
export function trackAction(
  action: string,
  category: string,
  properties?: Record<string, unknown>,
): void {
  trackEvent(action, { category, ...properties });
}

/**
 * Set user properties for event enrichment.
 */
export function setUserProperties(properties: Record<string, unknown>): void {
  _userProperties = { ..._userProperties, ...properties };
  if (ENVIRONMENT === 'development') {
    log.debug('User properties updated:', _userProperties);
  }
}

/**
 * Get current analytics session ID.
 * Lazily generates a session ID if one has not been created yet,
 * so callers never receive `null`.
 */
export function getSessionId(): string {
  if (!_sessionId) {
    _sessionId = generateUUID();
  }
  return _sessionId;
}

/**
 * Start a timed event.
 * Returns a function to call when the event is complete, which logs the duration.
 */
export function startTimedEvent(eventName: string): () => void {
  const startTime = Date.now();
  return () => {
    const duration = Date.now() - startTime;
    trackEvent(eventName, { duration });
    if (ENVIRONMENT === 'development') {
      log.debug('Timed event:', eventName, { duration });
    }
  };
}
